import { AsyncButton, ControlInlineEdit, getFromDataPath } from "aic";
import { useState } from "react";
import { Button, Collapse, Form } from "react-bootstrap";
import { ArrowLeftRight, ChevronDown, ChevronRight, PlusCircle } from "react-bootstrap-icons";
import { classMerge } from "../../utility/classMerge";
import { ConditionComparatorEnum } from "../ars_ldm";
import {
  ADD_EXTRA_WHERE_CLAUSE,
  PATCH_ARS_STATE,
  REMOVE_WHERE_CLAUSE,
  useArsContext,
} from "../contexts";
import { ArsSelect } from "../inputs/ArsSelect";
import { ArsText } from "../inputs/ArsText";
import { ArsVariable } from "../inputs/ArsVariable";
import { isSubClause } from "../typeGuards/isSubClause";
import { isSubClauseWithCondition } from "../typeGuards/isSubClauseWithCondition";
import { isWhereClauseCondition } from "../typeGuards/isWhereClauseCondition";
import { formatConditionAsCode } from "../utility/formatConditionAsCode";
import { parseConditionFromCode } from "../utility/parseConditionFromCode";
import styles from "./WhereClauseConditionItemControl.module.css";

/**
 * Props for the WhereClauseConditionItemControl component.
 */
export interface WhereClauseConditionItemControlProps extends React.HTMLAttributes<HTMLDivElement> {
  /** RFC 6901 JSON Pointer path to the WhereClauseCondition in ArsContext state */
  dataPath: string;
  /** If this where clause is part of a compound expression and can be removed */
  isRemovable?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * A control for editing a single WhereClauseCondition item, representing an inline condition
 * in the format: [dataset].[variable] [comparator] [value(s)].
 *
 * This component displays and manages the four fields of a where clause condition:
 * - dataset + variable: Combined selector using ArsVariable with dual paths
 * - comparator: The comparison operator (EQ, NE, GT, GE, LT, LE, IN, NOTIN)
 * - value: The value(s) for comparison (string array, displayed as newline-separated text via ArsText)
 *
 * The component starts in compact mode showing a code representation (e.g., "ADSL.SAFFL = 'Y'")
 * that can be clicked to expand to the full editor. The compact/expanded state is managed internally.
 *
 * Inline editing mode allows users to directly edit the code representation with real-time validation.
 *
 * All changes are synchronized with ArsContext state using the PATCH_ARS_STATE action.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the WhereClauseCondition in ArsContext state.
 * @param props.isRemovable - Indicates if this condition can be removed (if part of a compound expression).
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the where clause condition editor.
 */
export const WhereClauseConditionItemControl = ({
  dataPath,
  isRemovable = false,
  disabled = false,
  ...rest
}: WhereClauseConditionItemControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const subClausePath = dataPath.endsWith("/condition")
    ? dataPath.substring(0, dataPath.lastIndexOf("/condition"))
    : // istanbul ignore next
      "";
  const subClause = getFromDataPath(subClausePath, state, isSubClauseWithCondition);
  const canSwapToReference = subClause !== undefined;

  // Get the condition from state
  const whereClauseCondition = getFromDataPath(dataPath, state, isWhereClauseCondition);
  const thisId =
    getFromDataPath(dataPath.substring(0, dataPath.lastIndexOf("/")), state, isSubClause)?.id ?? "";
  const groupComparator =
    whereClauseCondition?.comparator === ConditionComparatorEnum.IN ||
    whereClauseCondition?.comparator === ConditionComparatorEnum.NOTIN;

  // Convert comparators enum to options for ArsSelect
  const comparatorOptions = state.comparators.map((comp) => ({
    value: comp,
    label: comp,
  }));

  // Compact mode with toggle (default view)
  const compactCode = formatConditionAsCode(whereClauseCondition, state.variables);

  // Get available variables for validation
  const availableVariables = state.variables.map((v) => v.value);

  // Validate current edit value
  const parsedCondition =
    isEditing && editValue ? parseConditionFromCode(editValue, availableVariables) : undefined;
  const isValid = parsedCondition !== false;
  const isInvalid = isEditing && editValue.length > 0 && !isValid;

  /**
   * Saves the changes to the condition.
   *
   * @param value - The value to save
   */
  const saveChanges = (value: string): void => {
    const parsed = parseConditionFromCode(value, availableVariables);
    if (parsed) {
      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath,
        value: parsed.condition,
      });
    }
  };

  /**
   * Handles removing a where clause.
   * Reverts to single condition if only 2 clauses remain.
   */
  const handleRemoveClause = (): void => {
    const parentPath = dataPath.substring(
      0,
      dataPath.lastIndexOf("/compoundExpression/whereClauses/"),
    );
    dispatch({
      operation: REMOVE_WHERE_CLAUSE,
      dataPath: parentPath,
      id: thisId,
    });
  };

  /**
   * Handles swapping a condition clause to a referenced entity.
   */
  const handleSwapToReference = (): void => {
    /* istanbul ignore else */
    if (subClause && subClausePath) {
      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: subClausePath,
        value: {
          id: subClause.id,
          level: subClause.level,
          order: subClause.order,
          subClauseId: "-- None --",
        },
      });
    }
  };

  /**
   * Converts the current SubClauseWithCondition into a SubClauseWithCompoundExpression.
   */
  const handleSwapToCompound = () => {
    dispatch({
      operation: ADD_EXTRA_WHERE_CLAUSE,
      dataPath: subClausePath,
      newLength: 2,
    });
  };

  return (
    <div {...rest}>
      <div className={styles.compactContainer}>
        <Button
          id={`${dataPath.replace(/\//g, "-")}-toggle-expand`}
          onClick={() => setIsExpanded(!isExpanded)}
          className={styles.toggleButton}
          variant="link"
          size="sm"
          aria-label={isExpanded ? "Collapse" : "Expand"}
          disabled={isEditing || disabled}
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Button>
        <div
          className="flex-grow-1"
          style={{ minWidth: 0 }}
        >
          <ControlInlineEdit
            id={`${dataPath.replace(/\//g, "-")}-edit-input`}
            aria-label="Enter condition"
            editLabel="Edit condition"
            deleteLabel="Remove clause"
            disabled={disabled}
            value={compactCode}
            setValue={saveChanges}
            onDraftChange={setEditValue}
            onEditChange={setIsEditing}
            onDelete={isRemovable && !isEditing ? handleRemoveClause : undefined}
            preventInvalidExit={false}
            whiteSpaceFix
            spellCheck={false}
            isInvalid={isInvalid}
            className={classMerge([isEditing && isValid && editValue.length > 0 ? "is-valid" : ""])}
            ReadComponent={({ value }) => (
              <div
                className="form-control form-control-sm"
                style={{ border: "none" }}
              >
                <code
                  className={styles.codeBlock}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {value === "" ? <span className="text-muted">No condition defined</span> : value}
                </code>
              </div>
            )}
          />
        </div>
        <div className={styles.actionButtons}>
          {!isEditing && (
            <>
              <ArrowLeftRight
                id={`${dataPath.replace(/\//g, "-")}-swap`}
                className={canSwapToReference ? "text-primary" : "text-muted"}
                role="button"
                aria-label="Swap to reference"
                aria-disabled={!canSwapToReference || disabled}
                onClick={canSwapToReference && !disabled ? handleSwapToReference : undefined}
                style={{ cursor: canSwapToReference && !disabled ? "pointer" : "not-allowed" }}
              />
            </>
          )}
        </div>
      </div>
      <Form.Text
        style={{
          height: isEditing ? "1.5em" : 0,
          minHeight: isEditing ? "1.5em" : 0,
          transition: "height 0.2s ease, min-height 0.2s ease",
        }}
        className={classMerge([
          isValid && editValue.length > 0
            ? "valid-feedback"
            : isInvalid
              ? "invalid-feedback"
              : "text-muted",
          styles.validationText,
        ])}
      >
        {isValid && editValue.length > 0
          ? "Valid condition"
          : isInvalid
            ? "Invalid condition format"
            : ""}
      </Form.Text>
      <Collapse in={isExpanded}>
        <div>
          <ArsVariable
            datasetDataPath={`${dataPath}/dataset`}
            variableDataPath={`${dataPath}/variable`}
            id={`${dataPath.replace(/\//g, "-")}-variable`}
            label="Variable"
            placeHolder="Select dataset and variable"
            disabled={isEditing || disabled}
          />
          <ArsSelect
            dataPath={`${dataPath}/comparator`}
            id={`${dataPath.replace(/\//g, "-")}-comparator`}
            label="Comparator"
            options={comparatorOptions}
            disabled={isEditing || disabled}
          />
          <ArsText
            dataPath={`${dataPath}/value`}
            isArray
            id={`${dataPath.replace(/\//g, "-")}-value`}
            label={groupComparator ? "Values" : "Value"}
            type="textarea"
            rows={groupComparator ? 3 : 1}
            placeholder="Enter values (one per line)"
            allowCarriageReturn={true}
            disabled={isEditing || disabled}
          />
          <AsyncButton
            aria-label="Add nested clause"
            className="mt-2 d-flex align-items-center"
            disabled={disabled}
            id={`${dataPath.replace(/\//g, "-")}-to-compound-expression`}
            onClick={handleSwapToCompound}
            size="sm"
            variant="success"
          >
            <span className="me-2">Add nested clause</span>
            <PlusCircle />
          </AsyncButton>
        </div>
      </Collapse>
    </div>
  );
};

WhereClauseConditionItemControl.displayName = "WhereClauseConditionItemControl";
